import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { FIELD_LEADING_ICON, FIELD_TRAILING_ICON } from '@/components/ui/fieldClasses';
import {
  PAGE_ACTIONS,
  PAGE_HEADER,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
  PAGE_SUBTITLE_STACKED,
  PAGE_TITLE,
  PAGE_TITLE_INLINE,
  PAGE_TOOLBAR,
  TOOLBAR_CLUSTER,
  TOOLBAR_CONTROL,
  toolbarSegmentClass,
} from './pageChrome';

export function PageHeader({
  title,
  subtitle,
  subtitleInline = true,
  actions,
  toolbar,
  children,
  surface: _surface = 'plain',
  titleClassName,
  subtitleClassName,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  subtitleInline?: boolean;
  actions?: ReactNode;
  toolbar?: ReactNode;
  children?: ReactNode;
  /** @deprecated White bar/card headers are removed. Kept so callers compile. */
  surface?: 'bar' | 'plain';
  titleClassName?: string;
  subtitleClassName?: string;
  className?: string;
}) {
  const subtitleEl = subtitle ? (
    subtitleInline ? (
      <span className={cn(PAGE_SUBTITLE, 'truncate', subtitleClassName)}>{subtitle}</span>
    ) : (
      <p className={cn(PAGE_SUBTITLE_STACKED, subtitleClassName)}>{subtitle}</p>
    )
  ) : null;

  return (
    <div className={cn(PAGE_HEADER, className)}>
      <div className={PAGE_HEADER_ROW}>
        <div className={subtitleInline ? PAGE_TITLE_INLINE : 'min-w-0 flex-1'}>
          <h1 className={cn(PAGE_TITLE, titleClassName)}>{title}</h1>
          {subtitleEl}
        </div>
        {actions ? <div className={PAGE_ACTIONS}>{actions}</div> : null}
      </div>
      {toolbar ? <div className={PAGE_TOOLBAR}>{toolbar}</div> : null}
      {children}
    </div>
  );
}

export function ToolbarCluster({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & { children: ReactNode }) {
  return (
    <div className={cn(TOOLBAR_CLUSTER, className)} {...props}>
      {children}
    </div>
  );
}

export function ToolbarSegment({
  active = false,
  className,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return <button type={type} className={cn(toolbarSegmentClass(active), className)} {...props} />;
}

export function PageSearch({
  value,
  onChange,
  onClear,
  placeholder,
  clearLabel,
  className,
  inputClassName,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  clearLabel?: string;
  inputClassName?: string;
}) {
  return (
    <div
      className={cn('relative min-w-[12rem] max-w-md flex-1', className)}
      data-leading-icon=""
      data-trailing-icon={value ? '' : undefined}
    >
      <Search className={cn(FIELD_LEADING_ICON, 'h-4 w-4')} />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          TOOLBAR_CONTROL,
          'w-full text-ds-text placeholder:font-normal placeholder:text-ds-placeholder focus:outline-none focus:shadow-ring',
          inputClassName,
        )}
        {...props}
        data-leading-icon=""
        data-trailing-icon={value ? '' : undefined}
      />
      {value && (
        <button
          type="button"
          onClick={() => (onClear ? onClear() : onChange(''))}
          className={cn(FIELD_TRAILING_ICON, 'hover:text-ds-text')}
          aria-label={clearLabel}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
